import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css'; // reuse same styles

function TermsOfService() {
  React.useEffect(() => {
    const { setCanonical } = require('../utils/seo');
    setCanonical('/terms');
  }, []);

  return (
    <div className="privacy-policy-page">
      <div className="privacy-header">
        <Link to="/login" className="privacy-back-link">← Back</Link>
        <h1>📜 Terms of Service</h1>
        <p className="privacy-subtitle">The rules that keep our community safe</p>
        <p className="privacy-effective">Effective Date: April 1, 2026 | Last Updated: May 6, 2026</p>
      </div>

      <div className="privacy-content">
        <section className="privacy-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By creating an account on Belong ("the Platform"), you agree to these Terms of Service. 
            If you do not agree, please do not use the Platform. We may update these terms from time 
            to time — continued use after changes constitutes acceptance.
          </p>
        </section>

        <section className="privacy-section">
          <h2>2. Eligibility</h2>
          <ul>
            <li>You must be at least 18 years old to use Belong</li>
            <li>You must provide a valid email address or phone number for verification</li>
            <li>You may only create one account per person</li>
            <li>You must provide accurate information during registration</li>
            <li>For Belong for Business: employees must use their verified work email to join an organization's private communities</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>3. Your Account</h2>
          <ul>
            <li>You are responsible for keeping your login credentials secure</li>
            <li>You are responsible for all activity under your account</li>
            <li>Notify us immediately if you suspect unauthorized access</li>
            <li>We reserve the right to suspend or terminate accounts that violate these terms</li>
          </ul>
        </section>

        <section className="privacy-section privacy-highlight">
          <h2>4. Community Guidelines</h2>
          <p className="golden-rule">
            💜 Belong is a safe space. Treat everyone with respect and dignity.
          </p>
        </section>

        <section className="privacy-section">
          <h2>5. Prohibited Conduct</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Harass, bully, threaten, or intimidate other members</li>
            <li>Share hate speech, slurs, or discriminatory content targeting any identity</li>
            <li>Create fake accounts or impersonate others</li>
            <li>Spam, solicit, or send unsolicited commercial messages</li>
            <li>Share another member's personal information without their consent (doxxing)</li>
            <li>Attempt to scrape, harvest, or collect data from the Platform</li>
            <li>Circumvent safety features, rate limits, or trust score restrictions</li>
            <li>Use the Platform for illegal activities</li>
            <li>Upload malicious content, viruses, or harmful code</li>
            <li>Pressure other members to reveal personal information or increase disclosure levels</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>6. Zero Tolerance Policy</h2>
          <p>We have zero tolerance for:</p>
          <ul>
            <li>Identity-based discrimination or harassment</li>
            <li>Threats of violence or harm</li>
            <li>Sexual harassment or unwanted sexual content</li>
            <li>Doxxing or sharing private information</li>
            <li>Exploitation of vulnerable community members</li>
          </ul>
          <p>
            Violations may result in immediate account suspension or permanent ban, 
            and we may cooperate with law enforcement when required by law.
          </p>
        </section>

        <section className="privacy-section">
          <h2>7. Content & Intellectual Property</h2>
          <ul>
            <li>You retain ownership of content you post (messages, posts, photos)</li>
            <li>By posting content, you grant Belong a license to display it within the Platform</li>
            <li>You must not post content that infringes on others' intellectual property</li>
            <li>We may remove content that violates these terms or community guidelines</li>
            <li>The Belong name, logo, and platform design are our intellectual property</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>8. Privacy & Data</h2>
          <p>
            Your use of Belong is also governed by our <Link to="/privacy">Privacy Policy</Link>, 
            which explains how we collect, use, and protect your data. Key points:
          </p>
          <ul>
            <li>Your identity information is encrypted and never shared publicly</li>
            <li>We use progressive disclosure — you control what you share and when</li>
            <li>We do not sell your data to third parties</li>
            <li>You can delete your account and data at any time from your Profile</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>9. Trust Score & Discovery Limits</h2>
          <p>
            To maintain community safety, new accounts have limited daily connection requests. 
            Your Trust Score increases as you complete your profile and 
            build genuine connections. This system exists to prevent spam and protect members.
          </p>
          <ul>
            <li>Trust Score is calculated automatically based on positive engagement</li>
            <li>Reported accounts may have their Trust Score reduced</li>
            <li>Attempting to manipulate the Trust Score system is a violation of these terms</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>10. Ambassador & Referral Program</h2>
          <p>
            Belong offers a self-serve Ambassador Program where users can earn rewards by referring 
            new members to the platform.
          </p>
          <ul>
            <li>Any registered user can generate a personal referral link</li>
            <li>Rewards are earned based on "activated" referrals — users who sign up AND make at least one accepted connection</li>
            <li>Pay rates: $20 per 100 activated users (US/International), $10 per 100 activated users (India)</li>
            <li>To claim rewards, email belong@ourbelong.com with your referral code and payment details</li>
            <li>Creating fake accounts, using bots, or any form of fraud to inflate referral counts is strictly prohibited and will result in permanent ban and forfeiture of all rewards</li>
            <li>Belong reserves the right to modify, suspend, or terminate the referral program at any time</li>
            <li>Belong reserves the right to withhold payment if fraudulent activity is detected</li>
            <li>Referral links must not be shared in a way that misrepresents Belong or uses deceptive practices (e.g., fake promises, misleading ads)</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>11. Belong for Business</h2>
          <p>
            Organizations can deploy private, anonymous communities for their employees through 
            Belong for Business. Additional terms apply:
          </p>
          <ul>
            <li>Organization admins can enable/disable identity categories and moderate reported content</li>
            <li>Organization admins CANNOT see individual employee posts, identities, connections, or activity</li>
            <li>Admins only receive aggregated, anonymized metrics subject to k-anonymity thresholds</li>
            <li>Employees must verify their work email (matching the org's domain) to join</li>
            <li>Org-specific spaces are private and separate from public Belong communities</li>
            <li>Organizations are responsible for communicating the availability of Belong to their employees</li>
            <li>Belong may terminate an organization's access for violations of these terms</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>12. AI Content Moderation</h2>
          <p>
            Belong uses artificial intelligence (OpenAI) to help moderate content and maintain community safety:
          </p>
          <ul>
            <li>Posts and comments are checked for harmful content (hate speech, violence, self-harm, etc.)</li>
            <li>Posts are evaluated for quality and genuine intent</li>
            <li>Personal contact information (phone numbers, emails, addresses) is automatically blocked in posts</li>
            <li>Connection request messages are checked for inappropriate content</li>
            <li>AI moderation is supplementary — human moderators review flagged content and reports</li>
            <li>Content sent to AI services is not used to train their models (per OpenAI's API data usage policy)</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>13. Reporting & Moderation</h2>
          <ul>
            <li>You can report any user or content that violates these terms</li>
            <li>You can block any user at any time — blocked users cannot see or contact you</li>
            <li>Our moderation team reviews all reports and takes appropriate action</li>
            <li>Messages are not end-to-end encrypted, allowing moderation of reported content</li>
            <li>False or malicious reports may result in action against the reporter</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>14. Disclaimer of Warranties</h2>
          <p>
            Belong is provided "as is" without warranties of any kind. While we strive to maintain 
            a safe and reliable platform, we cannot guarantee:
          </p>
          <ul>
            <li>Uninterrupted or error-free service</li>
            <li>The accuracy of information provided by other members</li>
            <li>The identity or intentions of other members</li>
            <li>That the Platform will meet all your expectations</li>
          </ul>
          <p>
            Always exercise caution when meeting people online. Meet in public places 
            and trust your instincts.
          </p>
        </section>

        <section className="privacy-section">
          <h2>15. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Belong and its team shall not be liable for 
            any indirect, incidental, special, or consequential damages arising from your use of 
            the Platform, including but not limited to interactions with other members, data loss, 
            or service interruptions.
          </p>
        </section>

        <section className="privacy-section">
          <h2>16. Account Termination</h2>
          <ul>
            <li>You may delete your account at any time from your Profile page</li>
            <li>We may suspend or terminate accounts that violate these terms</li>
            <li>Upon deletion, your personal data is anonymized as described in our Privacy Policy</li>
            <li>Certain anonymized data may be retained for safety and legal compliance</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>17. Changes to Terms</h2>
          <p>
            We may update these Terms of Service from time to time. When we make significant changes, 
            we will notify you through the app or via email. Your continued use of Belong after 
            changes are posted constitutes acceptance of the updated terms.
          </p>
        </section>

        <section className="privacy-section">
          <h2>18. Contact</h2>
          <p>Questions about these terms? Reach out:</p>
          <ul>
            <li>Email: <a href="mailto:belong@ourbelong.com">belong@ourbelong.com</a></li>
            <li>In-app: <Link to="/help">Contact Us</Link></li>
          </ul>
        </section>

        <div className="privacy-footer">
          <p>💜 These rules exist to keep Belong safe for everyone. Thank you for being part of this community.</p>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
